import type { InitOptions } from 'i18next';
import { resolveSupportedLocaleResult, buildI18nextConfig } from '@withjoy/locale';
import { SUPPORTED_LANGUAGE_CODES, GUEST_LANGUAGE_COOKIE_NAME, I18N_FALLBACK_LOCALE } from './i18n.constants';

// Static imports to avoid webpack warning and bundle bloat.
//
// Keyed by the BASE language code (`'es'`, not `'es-ES'`) because the consumer
// hook `useReactDatePickersLocale` indexes this map with `locale.language.split('-')[0]`.
// `date-fns/locale/es` is itself a base-language module — region-specific date-fns
// variants exist but are not loaded here; the base locale is a good default for
// every region of a language.
export const reactDatepickerLocaleImports: Record<string, () => Promise<{ default: Locale }>> = {
  es: () => import('date-fns/locale/es'),
  de: () => import('date-fns/locale/de'),
  fr: () => import('date-fns/locale/fr'),
  it: () => import('date-fns/locale/it'),
  ja: () => import('date-fns/locale/ja'),
};

export const getInitOptions = (overrides: Readonly<Partial<InitOptions>> = {}): InitOptions => {
  const base = buildI18nextConfig({
    supportedLocales: SUPPORTED_LANGUAGE_CODES,
    fallbackLocale: I18N_FALLBACK_LOCALE,
    // i18next's LanguageDetector defaults to lookupQuerystring='lng', which
    // collides with the hotel/accommodations search 'lng' longitude URL
    // param. Use 'l' (already recognized in LANGUAGE_PARAMS) instead. See
    // JW-47.
    lookupQuerystring: 'l',
  });

  return {
    ...base,
    // Server-side LanguageDetector order is querystring > cookie > header; pointing
    // lookupCookie at the guest-picker cookie makes SSR render the guest's saved
    // language on first paint. Merged (not replaced) to keep convertDetectedLanguage.
    detection: { ...(base.detection as object), lookupCookie: GUEST_LANGUAGE_COOKIE_NAME },

    // joy-web-specific overrides — not universal, kept out of the shared
    // package. Fields now locked in buildI18nextConfig and dropped from
    // here: load, nonExplicitSupportedLngs, cleanCode, interpolation,
    // supportedLngs, and the querystring detection block. fallbackLng is
    // deliberately overridden below (see notes).
    // Runtime value-fallback: NONE in prod. The verify-translations gate (#16531, prod-blocking)
    // guarantees every supported locale ⊇ en-US keys, and useTranslation walks the served
    // locale's bundle — so en-US never needs to load for non-English users (PRNT-4743 full
    // trim; see PR #16457 rounds 1-3 for why nothing weaker works). Dev keeps the en-US net:
    // translations only fire after merge to develop, so locally-added keys have en-US content
    // but no locale values yet. NOTE: this deliberately overrides buildI18nextConfig's locked
    // fallbackLng — detection of UNSUPPORTED locales still collapses to en-US via
    // fallbackLocale/convertDetectedLanguage above (those users are SERVED en-US).
    // No `preload`: the served language always loads via `lng`; preload's only prior job was
    // forcing en-US alongside it.
    fallbackLng: __DEV__ ? I18N_FALLBACK_LOCALE : false,
    returnEmptyString: false,
    partialBundledLanguages: true,

    react: {
      useSuspense: true,
      // wait=true in browsers, false on Node/SSR. The typeof guard (not just
      // `process?.`) matters post-P6: without the webpack process shim, a bare
      // `process` in the browser is an undeclared identifier (ReferenceError).
      // Truth table preserved from the shim era: shim had no `release` → true
      // in browsers; real Node has `release` → false on the server.
      wait: typeof process === 'undefined' || !process.release,
    },
    postProcess: [`reactPostprocessor`],
    ...overrides,
  };
};

type ParsedFormat = {
  dateFormat: string[];
  dateInputMask: string[];
};

export const getDateFormatString = (locale: string) => {
  const formatObj = new Intl.DateTimeFormat(locale).formatToParts(new Date());
  const parsed = formatObj.reduce(
    (acc, obj) => {
      switch (obj.type) {
        case 'day':
          acc.dateFormat.push('dd');
          acc.dateInputMask.push('99');
          break;
        case 'month':
          acc.dateFormat.push('MM');
          acc.dateInputMask.push('99');
          break;
        case 'year':
          acc.dateFormat.push('yyyy');
          acc.dateInputMask.push('9999');
          break;
      }
      return acc;
    },
    { dateFormat: [], dateInputMask: [] } as ParsedFormat,
  );
  return {
    dateFormat: parsed.dateFormat.join('/'),
    dateInputMask: parsed.dateInputMask.join('/'),
  };
};

/**
 * Resolves an arbitrary BCP 47 language code against the org-wide locale
 * registry (`@withjoy/locale`'s `SUPPORTED_LOCALES`, which is also
 * `SUPPORTED_LANGUAGE_CODES` here).
 *
 * Returned `ResolvedLocale.locale` is the canonical bundle key i18next actually
 * loads (e.g. `de-AT` → `de-DE`, bare `de` → `de-DE`, and product-intent
 * aliases like `en-IE` → `en-GB` / `en-NZ` → `en-AU` from
 * `LOCALE_TRANSLATION_MAP`). `.recognized` is `false` only when the input's
 * base language has no counterpart in the registry and would fall through to
 * the en-US source language.
 *
 * Use this whenever calling `i18n.changeLanguage()` from application code:
 * `changeLanguage` bypasses i18next's detection pipeline (and therefore the
 * `convertDetectedLanguage` closure inside `getInitOptions`), so raw regional
 * codes must be canonicalized here or i18next will fail to find a bundle and
 * fall back to English. See PR #15943 codex review round 1.
 */
export const resolveSupportedLocale = resolveSupportedLocaleResult;

/**
 * Whether a given BCP 47 language code is accepted by joy-web's i18n.
 *
 * Returns `true` when the input resolves to a supported locale by any means —
 * exact match, case-normalized match, closest sibling (e.g. de-AT → de-DE),
 * or base-language match (bare `de` → de-DE). Returns `false` only when the
 * input's base language has no counterpart in `SUPPORTED_LANGUAGE_CODES` and
 * would fall through to the default fallback.
 *
 * Liberal interpretation per PRNT-4468 — telemetry / i18n.language preserves
 * the user's reported region (e.g. 'en-CA'); content resolution collapses to
 * the canonical sibling. For that canonical form, use `resolveSupportedLocale`.
 */
export const languageSupported = (extendedLanguageCode: string): boolean => resolveSupportedLocale(extendedLanguageCode).recognized;
